'use client'

import type {ReactNode} from 'react'
import React, {createContext, useCallback, useContext, useEffect, useState} from 'react'
import {getSystemThemePreference} from '../../lib/utils'

type Theme = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

interface ThemeContextType {
    theme: Theme
    resolvedTheme: ResolvedTheme
    setTheme: (theme: Theme) => void
    toggleTheme: () => void
    forceTheme: (theme: ResolvedTheme | null) => void

    isDarkMode: boolean
    toggleDarkMode: () => void

}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
    children: ReactNode
    defaultTheme?: Theme
    storageKey?: string
    onToggleDarkMode?: () => void
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
                                                                children,
                                                                defaultTheme = 'light',
                                                                storageKey = 'fitro-theme',
                                                                onToggleDarkMode,
                                                            }) => {
    const [theme, setThemeState] = useState<Theme>(defaultTheme)
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light')
    const [forcedTheme, setForcedTheme] = useState<ResolvedTheme | null>(() => (
        typeof document !== 'undefined' && document.documentElement.dataset.fitroForcedTheme === 'light'
            ? 'light'
            : null
    ))
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const storedTheme = localStorage.getItem(storageKey)
            const initialTheme = storedTheme === 'dark' ? 'dark' : defaultTheme
            if (storedTheme && storedTheme !== 'dark') {
                localStorage.removeItem(storageKey)
            }
            setThemeState(initialTheme)
            setMounted(true)
        }
    }, [defaultTheme, storageKey])

    useEffect(() => {
        if (!mounted) return

        let resolved: ResolvedTheme

        if (theme === 'system') {
            resolved = getSystemThemePreference()
        } else {
            resolved = theme
        }

        setResolvedTheme(resolved)

    }, [theme, mounted])

    useEffect(() => {
        if (!mounted || theme !== 'system') return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        const handleChange = () => {
            const systemTheme = getSystemThemePreference()
            setResolvedTheme(systemTheme)

        }

        mediaQuery.addEventListener('change', handleChange)
        return () => mediaQuery.removeEventListener('change', handleChange)
    }, [theme, mounted])

    const effectiveTheme = forcedTheme ?? resolvedTheme

    useEffect(() => {
        if (!mounted) return

        const root = document.documentElement
        root.classList.remove('light', 'dark')
        root.classList.add(effectiveTheme)
        root.style.colorScheme = effectiveTheme
        // The theme is an in-app choice, not the OS scheme, so per-scheme status bar colours follow it.
        // A single unconditional theme-color (a light-only route, a demo banner) is deliberate and left alone.
        document.querySelectorAll('meta[name="theme-color"][media]').forEach(meta => {
            meta.setAttribute('content', effectiveTheme === 'dark' ? '#000000' : '#ffffff')
        })
    }, [effectiveTheme, mounted])

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme)
        if (typeof window === 'undefined') return
        if (newTheme === 'dark') {
            localStorage.setItem(storageKey, newTheme)
        } else {
            localStorage.removeItem(storageKey)
        }
    }

    const toggleTheme = () => {
        if (theme === 'system') {
            setTheme(resolvedTheme === 'light' ? 'dark' : 'light')
        } else {
            setTheme(theme === 'light' ? 'dark' : 'light')
        }
    }

    const forceTheme = useCallback((nextTheme: ResolvedTheme | null) => {
        setForcedTheme(nextTheme)
    }, [])

    const legacyToggleDarkMode = () => {
        if (onToggleDarkMode) {
            onToggleDarkMode()
        } else {
            toggleTheme()
        }
    }


    const contextValue: ThemeContextType = {
        theme: mounted ? theme : defaultTheme,
        resolvedTheme: mounted ? effectiveTheme : 'light',
        setTheme,
        toggleTheme,
        forceTheme,

        isDarkMode: mounted ? effectiveTheme === 'dark' : false,
        toggleDarkMode: legacyToggleDarkMode,
    }

    if (!mounted) {
        return (
            <ThemeContext.Provider value={contextValue}>
                {children}
            </ThemeContext.Provider>
        )
    }

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
